import * as React from "react";
import styled, { css } from "styled-components";
import { Field, useField } from "formik";
import { useQuery } from "@tanstack/react-query";
import trim from "lodash/trim";
import { Col, FormControl, FormGroup, Panel, Row } from "components/bootstrap";
import DocumentationLink from "components/support/DocumentationLink";
import DocsHelper from "util/DocsHelper";
import ToolsStore from "stores/tools/ToolsStore";
import useUserDateTime from "hooks/useUserDateTime";
import { InputDescription, Timestamp } from "components/common";
import debounceWithPromise from "views/logic/debounceWithPromise";
import { EMPTY_RANGE } from "views/components/time-range-picker/TimeRangeDisplay";
const Headline = styled.h3`
  margin-bottom: 5px;
`;
const KeywordInput = styled(FormControl)(
  ({ theme }) => css`
    min-height: 34px;
    font-size: ${theme.fonts.size.large};
  `
);
const EffectiveTimeRangeTable = styled.table`
  margin-bottom: 5px;

  td:first-child {
    padding-right: 10px;
  }
`;
const debouncedTestNaturalDate = debounceWithPromise(
  (keyword, userTZ) => ToolsStore.testNaturalDate(keyword, userTZ),
  350
);
const TimePreview = ({ dateTime, isLoading }) => {
  if (!dateTime || isLoading) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, EMPTY_RANGE);
  }
  return /* @__PURE__ */ React.createElement(Timestamp, { dateTime, format: "complete" });
};
const useKeywordPreview = (keyword, userTZ) => {
  const { data, isFetching } = useQuery({
    queryKey: ["time-range", "validation", "keyword", keyword],
    queryFn: () => debouncedTestNaturalDate(keyword, userTZ),
    retry: 0,
    enabled: !!trim(keyword)
  });
  return { data, isFetching };
};
const KeywordTimeRangePreview = () => {
  const [
    {
      value: { keyword }
    }
  ] = useField("timeRangeTabs.keyword");
  const { userTimezone } = useUserDateTime();
  const { data, isFetching } = useKeywordPreview(keyword, userTimezone);
  return /* @__PURE__ */ React.createElement(EffectiveTimeRangeTable, null, /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", null, "From"), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(TimePreview, { dateTime: data == null ? void 0 : data.from, isLoading: isFetching }))), /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", null, "To"), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(TimePreview, { dateTime: data == null ? void 0 : data.to, isLoading: isFetching })))));
};
const TabKeywordTimeRange = ({ disabled = false }) => /* @__PURE__ */ React.createElement(Row, { className: "no-bm" }, /* @__PURE__ */ React.createElement(Col, { sm: 5 }, /* @__PURE__ */ React.createElement(Headline, null, "Time range:"), /* @__PURE__ */ React.createElement(Field, { name: "timeRangeTabs.keyword.keyword" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
  FormGroup,
  {
    controlId: "form-inline-keyword",
    style: { marginRight: 5, width: "100%", marginBottom: 0 },
    validationState: error ? "error" : null
  },
  /* @__PURE__ */ React.createElement(
    KeywordInput,
    {
      type: "text",
      className: "input-sm mousetrap",
      name,
      disabled,
      placeholder: "Last week",
      title: "Keyword input",
      "aria-label": "Keyword input",
      onChange,
      required: true,
      value
    }
  ),
  /* @__PURE__ */ React.createElement(InputDescription, { error, help: "Specify the time frame for the search in natural language." })
)), /* @__PURE__ */ React.createElement("b", null, "Preview"), /* @__PURE__ */ React.createElement(KeywordTimeRangePreview, null)), /* @__PURE__ */ React.createElement(Col, { sm: 7 }, /* @__PURE__ */ React.createElement(Panel, null, /* @__PURE__ */ React.createElement(Panel.Body, null, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement("code", null, "last month"), " searches between one month ago and now"), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement("code", null, "4 hours ago"), " searches between four hours ago and now"), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement("code", null, "1st of april to 2 days ago"), " searches between 1st of April and 2 days ago"), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement("code", null, "yesterday midnight +0200 to today midnight +0200"), " searches between yesterday midnight and today midnight in timezone +0200 - will be 22:00 in UTC"), /* @__PURE__ */ React.createElement("p", null, "Please consult the", " ", /* @__PURE__ */ React.createElement(
  DocumentationLink,
  {
    page: DocsHelper.PAGES.TIME_FRAME_SELECTOR,
    title: "Keyword Time Range Documentation",
    text: "documentation"
  }
), " ", "for more details.")))));
export default TabKeywordTimeRange;
